// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  deleteFleetVehicleServiceAPI,
  getFleetVehicleServicesAPI,
} from "services/api/fleet/vehicles/services/api";
import { initialFleetVehicleServiceSearchForm } from "services/api/fleet/vehicles/services/consts";
import {
  FleetVehicleServiceRow,
  FleetVehicleServiceSearchFormValues,
} from "services/api/fleet/vehicles/services/types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { FLEET_SERVICE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import VehicleServiceEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleServiceEditCard/VehicleServiceEditCard";
import ServiceSearchResults from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleServiceSearchPage/components/ServiceSearchResults/ServiceSearchResults";

// Lokalna navigacija
import { Routes } from "ui/modules/fleet/services/navigation";

// Lokalne komponente
import ServiceSearchFilters from "./components/ServiceSearchFilters/ServiceSearchFilters";
import { ServiceSearchPageCmp, ServiceSearchPageProps } from "./types";

/**
 * Servisni nalozi kroz sva vozila flote. Isti popis kao na kartici vozila, samo bez
 * filtra po vozilu — pa svaki redak nosi i karticu vozila.
 */
const ServiceSearchPage: ServiceSearchPageCmp = (
  _props: ServiceSearchPageProps
) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleServiceRow>
  >(initialDataWithPagination);
  const [filterValues, setFilterValues] =
    useState<FleetVehicleServiceSearchFormValues>(
      initialFleetVehicleServiceSearchForm
    );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [serviceId, setServiceId] = useState<string>("");
  const [showEditCard, setShowEditCard] = useState<boolean>(false);
  const [deleteServiceId, setDeleteServiceId] = useState<string | null>(null);

  useEffect(() => {
    fetchSearchResult(initialDataWithPagination.pagination, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: FleetVehicleServiceSearchFormValues
  ) => {
    setIsLoading(true);
    try {
      // Bez vehicleId endpoint vraća naloge cijelog tenanta.
      setSearchResults(await getFleetVehicleServicesAPI(pagination, values));
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback(
    (values: FleetVehicleServiceSearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults.pagination]
  );

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchSearchResult(pagination, filterValues);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues]
  );

  // Nalog nosi svoje vozilo, pa se uređuje istim čarobnjakom kao s kartice vozila.
  const handleRowClick = useCallback((id: string) => {
    setServiceId(id);
    setShowEditCard(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditCard(false);
    setServiceId("");
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditCard(false);
    setServiceId("");
    fetchSearchResult(searchResults.pagination, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults.pagination, filterValues]);

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteServiceId(id);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteServiceId) return;

    try {
      await deleteFleetVehicleServiceAPI(deleteServiceId, t);
      fetchSearchResult(searchResults.pagination, filterValues);
    } catch (err) {
      console.error("Greška prilikom brisanja servisnog naloga:", err);
    }

    setDeleteServiceId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteServiceId, t, searchResults.pagination, filterValues]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteServiceId(null);
  }, []);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={FLEET_SERVICE_SEARCH_PAGE_TITLE.text}
              icon={FLEET_SERVICE_SEARCH_PAGE_TITLE.icon}
              iconColor={FLEET_SERVICE_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <ServiceSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema servisnih naloga" />
          ) : (
            <ServiceSearchResults
              searchResults={searchResults}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onDeleteClick={handleDeleteClick}
              showVehicle
              onVehicleClick={handleVehicleClick}
            />
          )}
        </div>
      </div>

      {showEditCard && (
        <VehicleServiceEditCard
          show={showEditCard}
          vehicleId=""
          serviceId={serviceId}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      )}

      <Dialog
        open={!!deleteServiceId}
        title="Brisanje servisnog naloga"
        message="Jeste li sigurni da želite obrisati servisni nalog?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

ServiceSearchPage.route = Routes.ServiceSearchPageRouter;

export default ServiceSearchPage;
